import React from "react";
import Cropper from "react-easy-crop";
import {
    TrimCancelButton,
    VideoControlBox,
    PlayPauseButton,
    ProgressBarWrapper,
    ProgressBar,
    TimeDisplay,
    IconButton,
    TrimBox,
    TimeLabel,
    Handle,
    VideoControlsRow,
    VideoCropPreviewBox,
    PlayIcon,
    SaveIcon,
    PauseIcon,
} from "./VideoCrop.style";

import { ZoomControls, ZoomButton } from "../../components/ImageCropModal/ModalCrop.style";
import EditIcon from "src/img/cut.svg";
import Close from "src/img/remove-hero-image.png";
import { PlusIcon, MinusIcon } from "../ImageCropModal/CropperSection";
import { LottieLoadingIndicator } from "src/components";

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

export const OriginalVideoCropper = ({
    videoSrcInternal,
    crop,
    zoom,
    setCrop,
    setZoom,
    computedAspect,
    handleCropComplete,
    handleCropSizeChange,
    videoRef,
    currentTime,
    duration,
    isPlaying,
    trimStart,
    setTrimStart,
    trimEnd,
    setTrimEnd,
    showTrimmer,
    setShowTrimmer,
    isTrimming,
    handleTrim,
    capturedFrames,
    progressBarRef,
    hiddenVideoRef,
    formatTime,
    handleTrimDrag,
    togglePlay,
    trimmedVideoSrc,
    onCancelTrim,
    cropperKey
}) => {
    // Calculated values for trimming box UI
    const hasDuration = duration > 0;
    const trimBoxPercent = hasDuration ? ((trimEnd - trimStart) / duration) * 100 : 0;
    const trimBoxLeft = hasDuration ? (trimStart / duration) * 100 : 0;

    // Frame width percentage for thumbnail strip inside trim box
    const trimDurationRatio = hasDuration ? (trimEnd - trimStart) / duration : 0;
    const visibleFrameCount = Math.ceil(trimDurationRatio * capturedFrames.length);
    const frameWidthPercent = visibleFrameCount > 0 ? 100 / visibleFrameCount : 100;

    const safeDuration = duration || 1;
    const progressPercent = trimEnd && currentTime >= trimEnd
        ? 100
        : ((currentTime - trimStart) / (trimEnd ? trimEnd - trimStart : safeDuration)) * 100;

    return (
        <>
            {videoSrcInternal && (
                <>
                    <div style={{ marginBottom: "20px" }}>
                        <Cropper
                            key={cropperKey}
                            video={videoSrcInternal}
                            setVideoRef={(ref) => {
                                if (ref && ref.current) videoRef.current = ref.current;
                            }}
                            crop={crop}
                            zoom={zoom}
                            aspect={computedAspect}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={(croppedArea, croppedAreaPixels) => {
                                if (typeof handleCropComplete === "function") {
                                    handleCropComplete(croppedArea, croppedAreaPixels);
                                }
                            }}
                            onCropSizeChange={handleCropSizeChange}
                            showGrid={false}
                            mediaProps={{
                                controls: false,
                                muted: false,
                                autoPlay: false,
                                loop: false,
                                playsInline: true,
                            }}
                            style={{ containerStyle: { backgroundColor: "transparent" } }}
                        />
                    </div>
                    { trimmedVideoSrc ? (
                        <VideoControlBox>
                            <VideoControlsRow>
                                <PlayPauseButton onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
                                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                                </PlayPauseButton>
                            
                                <ProgressBarWrapper>
                                    <ProgressBar percent={Math.min(100, Math.max(0, progressPercent))} />
                                    <TimeDisplay>
                                        <span>{formatTime(Math.max(currentTime - trimStart, 0))}</span>
                                        <span>{formatTime(trimEnd ? trimEnd - trimStart : duration - trimStart)}</span>
                                    </TimeDisplay>
                                </ProgressBarWrapper>
                            
                                <button
                                    onClick={onCancelTrim}
                                    aria-label="Cancel Trim"
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        marginLeft: 8,
                                        cursor: "pointer",
                                    }}
                                > Reset
                                </button>
                            </VideoControlsRow>
                        </VideoControlBox>
                    ) : (
                        <VideoControlBox>
                            <VideoControlsRow>
                                <PlayPauseButton onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
                                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                                </PlayPauseButton>

                                <ProgressBarWrapper ref={progressBarRef} style={{ position: "relative" }}>
                                    {(trimEnd > trimStart) ? (
                                        <div
                                            style={{
                                                position: "absolute",
                                                left: `${(trimStart / duration) * 100}%`,
                                                width: `${((trimEnd - trimStart) / duration) * 100}%`,
                                            }}
                                        >
                                            <ProgressBar
                                                percent={Math.max(0, Math.min(100,
                                                    (currentTime >= trimEnd)
                                                        ? 100
                                                        : ((currentTime - trimStart) / (trimEnd - trimStart)) * 100
                                                ))}
                                            />
                                        </div>
                                    ) : (
                                        <ProgressBar percent={(currentTime / duration) * 100} />
                                    )}

                                    {showTrimmer && (
                                        <>
                                            {capturedFrames.length > 0 && (
                                                <VideoCropPreviewBox left={trimBoxLeft} width={trimBoxPercent}>
                                                    {capturedFrames
                                                        .slice(
                                                            Math.floor((trimStart / duration) * capturedFrames.length),
                                                            Math.ceil((trimEnd / duration) * capturedFrames.length)
                                                        )
                                                        .map((frameSrc, idx) => (
                                                            <img
                                                                key={idx}
                                                                src={frameSrc}
                                                                alt={`Frame ${idx}`}
                                                                style={{
                                                                    height: "100%",
                                                                    display: "inline-block",
                                                                    objectFit: "cover",
                                                                    userSelect: "none",
                                                                    pointerEvents: "none",
                                                                    width: `${frameWidthPercent}%`,
                                                                }}
                                                                draggable={false}
                                                            />
                                                        ))}
                                                </VideoCropPreviewBox>
                                            )}
                                            <TrimBox left={trimBoxLeft} width={trimBoxPercent} visible={trimEnd > trimStart}>
                                                <TimeLabel style={{ left: -2, top: 12 }}>
                                                    {(isPlaying && currentTime >= trimStart && currentTime <= trimEnd)
                                                        ? formatTime(currentTime)
                                                        : formatTime(trimStart)}
                                                </TimeLabel>
                                                <Handle style={{ left: -8 }} onMouseDown={(e) => handleTrimDrag(e, "start")} />
                                                <TimeLabel style={{ right: -2, top: 12 }}>{formatTime(trimEnd)}</TimeLabel>
                                                <Handle style={{ right: -8 }} onMouseDown={(e) => handleTrimDrag(e, "end")} />

                                                <TrimCancelButton 
                                                    onClick={() => {
                                                        if (videoRef.current) {
                                                            videoRef.current.pause();
                                                            videoRef.current.currentTime = 0;
                                                        }
                    
                                                        setShowTrimmer(false);
                                                        setTrimStart(0);
                                                        setTrimEnd(duration);
                                                    }} aria-label="Cancel Trim">
                                                    <img src={Close} alt="Close" width="24px" />
                                                </TrimCancelButton>
                                            </TrimBox>
                                        </>
                                    )}

                                    <TimeDisplay>
                                        <span>{formatTime(showTrimmer ? 0 : currentTime)}</span>
                                        <span>{formatTime(duration)}</span>
                                    </TimeDisplay>

                                </ProgressBarWrapper>

                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    <IconButton
                                        onClick={() => {
                                            if (showTrimmer) {
                                                handleTrim();
                                            } else {
                                                setTrimStart(0);
                                                setTrimEnd(duration);
                                                setShowTrimmer(true);
                                            }
                                        }}
                                        disabled={isTrimming || (showTrimmer && trimEnd <= trimStart)}
                                        title={showTrimmer ? "Save Trim" : "Trim Video"}
                                    >
                                        {showTrimmer
                                            ? isTrimming
                                                ? <LottieLoadingIndicator width="40px" height="40px" style={{ marginTop: "-24px" }} />
                                                : <SaveIcon />
                                            : <img src={EditIcon} alt="Trim" width="24px" />
                                        }
                                    </IconButton>
                                </div>
                            </VideoControlsRow>
                        </VideoControlBox>
                    )}
                    
                    <ZoomControls>
                        <ZoomButton
                            onClick={() => setZoom(Math.max(zoom - 0.1, MIN_ZOOM))}
                            disabled={zoom <= MIN_ZOOM}
                            aria-label="Zoom Out"
                        >
                            <MinusIcon />
                        </ZoomButton>

                        <ZoomButton
                            onClick={() => setZoom(Math.min(zoom + 0.1, MAX_ZOOM))}
                            disabled={zoom >= MAX_ZOOM}
                            aria-label="Zoom In"
                        >
                            <PlusIcon />
                        </ZoomButton>
                    </ZoomControls>

                    {/* Hidden video for capturing frames */}
                    <video
                        ref={hiddenVideoRef}
                        style={{ display: "none" }}
                        muted
                        playsInline
                        src={videoSrcInternal}
                    />
                </>
            )}
        </>
    );
};
